export const RESIDENCE_PERMIT_EXPIRING_THRESHOLD_DAYS = 60;

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const REVIEWED_OR_PENDING_STATES = ["1", "2", "3"];

// Calendar days are stored at midnight UTC: only the first 10 characters carry the day.
// Never go through new Date(iso) in local time, it shows the previous day in the Antilles (UTC-4).
export const toCalendarDate = (value) => {
    if (typeof value !== "string" || !CALENDAR_DATE_PATTERN.test(value)) return null;
    return value.slice(0, 10);
};

export const formatCalendarDate = (iso) => {
    const calendarDate = toCalendarDate(iso);
    if (!calendarDate) return "";
    const [year, month, day] = calendarDate.split("-");
    return `${day}/${month}/${year}`;
};

// A date picked or computed in the browser is the user's own calendar day: built from its local
// components, never from toISOString, which is UTC and can shift it by one day.
export const localDateToCalendarDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// Local midnight of a calendar day, for date pickers working in local time.
export const calendarDateToLocalDate = (value) => {
    const calendarDate = toCalendarDate(value);
    if (!calendarDate) return null;
    const [year, month, day] = calendarDate.split("-").map(Number);
    return new Date(year, month - 1, day);
};

const toTodayCalendarDate = (today) => {
    if (typeof today === "string") return toCalendarDate(today);
    return localDateToCalendarDate(today instanceof Date ? today : new Date());
};

const calendarDateToUtcMs = (calendarDate) => {
    const [year, month, day] = calendarDate.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
};

const latestCalendarDate = (dates) =>
    dates.filter(Boolean).reduce((latest, date) => (!latest || date > latest ? date : latest), null);

const createdAtMs = (document) => {
    const ms = Date.parse(document?.createdAt);
    return Number.isNaN(ms) ? -Infinity : ms;
};

const getPermitState = (effectiveEnd, todayCalendarDate) => {
    if (!effectiveEnd || !todayCalendarDate) return "unknown";
    const daysLeft = (calendarDateToUtcMs(effectiveEnd) - calendarDateToUtcMs(todayCalendarDate)) / MS_PER_DAY;
    // The end date is the last valid day: the permit only expires the day after.
    if (daysLeft < 0) return "expired";
    if (daysLeft <= RESIDENCE_PERMIT_EXPIRING_THRESHOLD_DAYS) return "expiring";
    return "valid";
};

export const getResidencePermitStatus = (tempData, today = new Date()) => {
    const visa = tempData?.visa;
    const hasPermit = typeof visa?.type === "string" ? visa.type.trim() !== "" : Boolean(visa?.type);
    const documents = Array.isArray(visa?.complementaryDocuments) ? visa.complementaryDocuments : [];

    const permitEnd = toCalendarDate(visa?.end_validity);
    const validatedEnds = documents
        .filter((document) => String(document?.validated) === "2")
        .map((document) => toCalendarDate(document?.end_validity));
    const effectiveEnd = latestCalendarDate([permitEnd, ...validatedEnds]);

    // Drafts ('0') are invisible until the temp worker confirms them, so they never count as the latest document.
    // On equal createdAt, the later entry in the array wins.
    const latestDocument = documents
        .filter((document) => REVIEWED_OR_PENDING_STATES.includes(String(document?.validated)))
        .reduce((latest, document) => (!latest || createdAtMs(document) >= createdAtMs(latest) ? document : latest), null);

    const hasPending = documents.some((document) => String(document?.validated) === "1");

    return {
        hasPermit,
        permitEnd,
        effectiveEnd,
        state: getPermitState(effectiveEnd, toTodayCalendarDate(today)),
        latestDocument,
        hasPending,
        canAddExtension: hasPermit && !hasPending,
    };
};
